import { createSlice } from "@reduxjs/toolkit";
import { influencerSlice } from "types/influencer";
import {
    fetchBasicSocialPostThunk,
    fetchCollaborationSocialPostsThunk,
    fetchInfluencerSocialAccount,
    fetchSocialAccountApplicationsThunk,
    fetchSocialAccountCollaborationsThunk,
    fetchSocialPostsThunk,
    fetchSocialPostThunk,
} from "./thunk";
import { ERROR } from "types";

const initialState: influencerSlice = {
    loading: false,
    error: null,
    insights: {},
    postLoading: false,
    postError: null,
    mediaLoading: false,
    mediaError: null,
    posts: {},
    basic_posts: {},
    media: {},
    basic_social_posts: {},
    social_posts: {},
    social_media: {},
    social_accounts: {},
    social_account_applications: {},
    social_account_collaborations: {},
};

const influcencerSlice = createSlice({
    name: 'influencers',
    initialState,
    reducers: {
        setMediaLoading: (state, data) => {
            state.mediaLoading = data.payload;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchBasicSocialPostThunk.fulfilled, (state, {payload}) => {
            state.postLoading = false;
            state.postError = null;

            const {post, postId} = payload;

            state.basic_social_posts[postId] = {
                post,
                last_fetched_at: Date.now()
            }
        })
        .addCase(fetchSocialPostThunk.pending, (state) => {
            state.postLoading = true;
            state.postError = null;
        })
        .addCase(fetchSocialPostThunk.fulfilled, (state, {payload}) => {
            state.postLoading = false;
            state.postError = null;

            const {socialAccountId, post, postId} = payload;

            const existingPosts = state.social_posts[socialAccountId] ?? {};

            state.social_posts[socialAccountId] = {
                ...existingPosts,
                [postId]: {
                    post,
                    last_fetched_at: Date.now()
                }
            }
        })
        .addCase(fetchSocialPostThunk.rejected, (state, {payload}) => {
            state.postLoading = false;
            state.postError = payload || "Failed to fetch post"
        })
        .addCase(fetchSocialPostsThunk.pending, (state) => {
            state.mediaLoading = true;
            state.mediaError = null;
        })
        .addCase(fetchSocialPostsThunk.fulfilled, (state, {payload}) => {
            state.mediaLoading = false;
            state.mediaError = null;

            const {socialAccountId, posts, cacheKey, page} = payload;

            const existingMedia = state.social_media[socialAccountId] ?? {};
            const existingList = existingMedia[cacheKey]?.items ?? [];
            const finalList = page === 1 ? posts.items : [...existingList, ...posts.items];

            state.social_media[socialAccountId] = {
                ...existingMedia,
                [cacheKey]: {
                    items: finalList,
                    count: posts.count,
                    has_more: posts.count > finalList.length,
                    last_fetched_at: Date.now(),
                    expired: false,
                    page,
                    last_fetched_at_server: '',
                }
            }
        })
        .addCase(fetchSocialPostsThunk.rejected, (state, {payload}) => {
            state.mediaLoading = false;
            if (payload !== ERROR.CACHED) {
                state.mediaError = payload || "Failed to fetch media";
            }
        })
        .addCase(fetchCollaborationSocialPostsThunk.pending, (state) => {
            state.loading = true;
        })
        .addCase(fetchCollaborationSocialPostsThunk.fulfilled, (state, action) => {
            const socialAccountId = action.payload.socialAccountId;
            const mediaProductType = action.payload.mediaProductType;
            const existing_items = state.social_media?.[socialAccountId]?.[mediaProductType]?.items
                ? [...state.social_media[socialAccountId][mediaProductType].items]
                : [];
            const items = action.payload.page === 1
                ? action.payload.media.items
                : [...existing_items, ...action.payload.media.items];
            state.social_media = {
                ...(state.social_media ?? {}),
                [socialAccountId]: {
                    ...(state.social_media?.[socialAccountId] ?? {}),
                    [mediaProductType]: {
                        items,
                        page: action.payload.page,
                        last_fetched_at: Date.now(),
                        has_more: items.length < action.payload.media.count,
                        count: action.payload.media.count,
                        last_fetched_at_server: action.payload.media.last_fetched_at,
                        expired: false,
                    },
                },
            };
            state.loading = false;
        })
        .addCase(fetchCollaborationSocialPostsThunk.rejected, (state) => {
            state.loading = false;
        })
        .addCase(fetchInfluencerSocialAccount.fulfilled, (state, {payload}) => {
            const {socialAccount, socialAccountId} = payload;
            state.social_accounts[socialAccountId] = {
                social_account: socialAccount,
                last_fetched_at: Date.now()
            }
        })
        .addCase(fetchSocialAccountApplicationsThunk.pending, (state, { meta }) => {
            const socialAccountId = meta.arg.socialAccountId;
            const existing = state.social_account_applications[socialAccountId];
            state.social_account_applications[socialAccountId] = {
                items: existing?.items ?? [],
                last_fetched_at: existing?.last_fetched_at ?? 0,
                loading: true,
                error: null,
            };
        })
        .addCase(fetchSocialAccountApplicationsThunk.fulfilled, (state, { payload }) => {
            const { socialAccountId, items } = payload;
            state.social_account_applications[socialAccountId] = {
                items,
                last_fetched_at: Date.now(),
                loading: false,
                error: null,
            };
        })
        .addCase(fetchSocialAccountApplicationsThunk.rejected, (state, { meta, payload }) => {
            const socialAccountId = meta.arg.socialAccountId;
            const existing = state.social_account_applications[socialAccountId];
            state.social_account_applications[socialAccountId] = {
                items: existing?.items ?? [],
                last_fetched_at: existing?.last_fetched_at ?? 0,
                loading: false,
                error: payload === ERROR.CACHED ? null : (payload || 'Failed to fetch applications'),
            };
        })
        .addCase(fetchSocialAccountCollaborationsThunk.pending, (state, { meta }) => {
            const socialAccountId = meta.arg.socialAccountId;
            const existing = state.social_account_collaborations[socialAccountId];
            state.social_account_collaborations[socialAccountId] = {
                items: existing?.items ?? [],
                last_fetched_at: existing?.last_fetched_at ?? 0,
                loading: true,
                error: null,
            };
        })
        .addCase(fetchSocialAccountCollaborationsThunk.fulfilled, (state, { payload }) => {
            const { socialAccountId, items } = payload;
            state.social_account_collaborations[socialAccountId] = {
                items,
                last_fetched_at: Date.now(),
                loading: false,
                error: null,
            };
        })
        .addCase(fetchSocialAccountCollaborationsThunk.rejected, (state, { meta, payload }) => {
            const socialAccountId = meta.arg.socialAccountId;
            const existing = state.social_account_collaborations[socialAccountId];
            state.social_account_collaborations[socialAccountId] = {
                items: existing?.items ?? [],
                last_fetched_at: existing?.last_fetched_at ?? 0,
                loading: false,
                error: payload === ERROR.CACHED ? null : (payload || 'Failed to fetch collaborations'),
            };
        })
    }
})

export const { setMediaLoading } = influcencerSlice.actions;

export default influcencerSlice.reducer;
